/**
 * Customers — new (S09 → "Add customer" CTA target).
 *
 * Dedicated route rather than a modal: PWA-friendly (deep-linkable, browser
 * back works), and `@flint/ui` doesn't ship a Dialog primitive yet — using
 * a route avoids introducing one for this single flow.
 *
 * @see prototype/sales/customer-new.html — V1.1 customer composer baseline
 * @see prototype/sales/customers.html "Add customer" button — opens this route
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { trpc } from "../../../../lib/trpc.ts";
import { CustomerComposer } from "../../../../features/customers/composer/index.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";

export const Route = createFileRoute("/(workspace)/sales/customers/new")({
  component: NewCustomerPage,
});

function NewCustomerPage() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const create = trpc.customers.create.useMutation({
    onSuccess: async () => {
      // Refetch the list + counts so the new row appears the moment we
      // navigate. The detail page also fires its own get-by-id, so the
      // landing page reads fresh data either way.
      await Promise.all([utils.customers.list.invalidate(), utils.customers.counts.invalidate()]);
    },
  });

  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Sales", to: "/" },
        { label: "Customers", to: "/sales/customers" },
        { label: "New" },
      ]}
      contentClassName="max-w-none h-full"
      viewportClassName="p-0"
      disableViewportScroll
    >
      <CustomerComposer
        pending={create.isPending}
        serverError={create.error?.message ?? null}
        onSubmit={async (payload) => {
          const result = await create.mutateAsync(payload);
          void navigate({
            to: "/sales/customers/$customerId",
            params: { customerId: result.id },
          });
        }}
      />
    </WorkspaceShell>
  );
}
